import { useTranslation } from "react-i18next";
import { CircularProgress, IconButton, Modal, Tooltip } from "@mui/material";
import {
  Wastebasket,
  AddModalIcon,
  EditModalIcon,
  DuplicateModalIcon,
  WarningModalIcon,
  DeleteModalIcon,
} from "@/icons";
import { GomakePrimaryButton } from "../button";
import { useStyle } from "./style";
import { CloseIcon } from "@/widgets/quote-new/receipts-table/Payment-methods-details/icons/close-icon";
import { ModalType } from "@/enums/tabs-icon-enums";
import { RoundedCloseIcon } from "../modal/icon/close";
import { HpCloseIcon } from "@/themes/hp-client/icons";
import { _renderSubTitle } from "@/utils/constants";
import { _renderTitle } from "@/utils/constants";
import { _renderIcon } from "@/utils/constants";
import { useTheme } from "@/contexts/theme";
import { PrimaryButton } from "../button/primary-button";

const GoMakeDeleteModal = ({
  openModal,
  onClose,
  insideStyle,
  subTitle,
  onClickDelete,
  hideIcon = false,
  title,
  yesBtn,
  onClickCancel,
  /** X, Escape, and a click outside. Falls back to `onClose` when omitted. */
  onDismiss,
  children,
  withClose = true,
  withRoundedCloseIcon = false,
  modalType = ModalType.DELETE,
  /** When false, confirm runs `onClickDelete` only; parent should close (e.g. after async success). */
  closeAfterDelete = true,
  confirmButtonDisabled = false,
  confirmButtonLoading = false,
  /** Stack above parent modals (e.g. send-material dialog ~9_999_999). */
  zIndex: zIndexOverride,
  style: modalStyleFromProps,
  ...props
}: any) => {
  const { currentTheme } = useTheme();
  const isHp = currentTheme === "hp-client";
  const { classes } = useStyle({ modalType, isHp });
  const { t } = useTranslation();

  const _renderConfirmBtn = (modalType: ModalType) => {
    if (modalType === ModalType.DELETE) {
      return t("modal.delete");
    }
    if (modalType === ModalType.DUPLICATE) {
      return t("modal.duplicateConfirmBtn");
    }
    if (modalType === ModalType.EDIT) {
      return isHp ? t("modal.saveChanges") : t("modal.editConfirmBtn");
    }
    if (modalType === ModalType.ADD) {
      return t("modal.addConfirmBtn");
    }
    if (modalType === ModalType.WARNING) {
      return t("modal.warningConfirmBtn");
    }
  };

  const _renderCancelBtn = (modalType: ModalType) => {
    if (modalType === ModalType.DELETE) {
      return t("modal.cancel");
    } else {
      return t("modal.discard");
    }
  };

  const handleCancelClick = () => {
    if (confirmButtonLoading) {
      return;
    }
    if (onClickCancel) {
      onClickCancel();
    }
    onClose();
  };

  const handleDismiss = () => {
    if (confirmButtonLoading) {
      return;
    }
    if (onDismiss) {
      onDismiss();
      return;
    }
    onClose();
  };

  const confirmLabel = yesBtn ? t(yesBtn) : _renderConfirmBtn(modalType);
  const isConfirmDisabled = confirmButtonDisabled || confirmButtonLoading;
  const loadingLabel = t("modal.loading");
  const confirmButtonContent = confirmButtonLoading ? (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <CircularProgress size={18} color="inherit" />
      {loadingLabel}
    </span>
  ) : (
    confirmLabel
  );

  const showSubTitleInHp =
    isHp &&
    (modalType === ModalType.DELETE ||
      modalType === ModalType.WARNING ||
      subTitle);

  return (
    <Modal
      disableEnforceFocus
      open={openModal}
      onClose={handleDismiss}
      {...props}
      style={{
        outline: "none",
        zIndex:
          zIndexOverride ??
          modalStyleFromProps?.zIndex ??
          999999,
      }}
    >
      <div style={{ ...classes.container, ...modalStyleFromProps }}>
        <div style={classes.content}>
          {withClose && (
            <div style={classes.closeIcon} onClick={handleDismiss}>
              <Tooltip title={t("modal.close")}>
                <IconButton
                  size="small"
                  sx={{ color: isHp ? "#1A1A1A" : undefined }}
                >
                  {isHp ? (
                    <HpCloseIcon />
                  ) : withRoundedCloseIcon ? (
                    <RoundedCloseIcon />
                  ) : (
                    <CloseIcon />
                  )}
                </IconButton>
              </Tooltip>
            </div>
          )}
          {isHp ? (
            <>
              {"header" in classes && (
                <div
                  style={(classes as { header?: React.CSSProperties }).header}
                >
                  <div style={classes.title}>
                    {title ? t(title) : _renderTitle(modalType)}
                  </div>
                  {showSubTitleInHp && (
                    <div style={classes.subTitle}>
                      {subTitle ? t(subTitle) : _renderSubTitle(modalType)}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              {!hideIcon && (
                <div style={classes.icon}>
                  {props.icon || _renderIcon(modalType)}
                </div>
              )}
              <div style={classes.title}>
                {title ? t(title) : _renderTitle(modalType)}
              </div>
              <div style={classes.subTitle}>
                {subTitle ? t(subTitle) : _renderSubTitle(modalType)}
              </div>
            </>
          )}

          {children && (
            <div style={{ ...classes.children, ...insideStyle }}>
              {children}
            </div>
          )}
          <div
            style={{ ...classes.btnsContainer, ...props.btnsContainerStyle }}
          >
            {isHp ? (
              <>
                <PrimaryButton
                  variant={modalType === ModalType.DELETE ? "outlined" : "text"}
                  disabled={confirmButtonLoading}
                  style={{
                    ...props.cancelBtnStyle,
                    ...classes.cancelBtn,
                  }}
                  onClick={handleCancelClick}
                >
                  {props?.cancelBtn ?? _renderCancelBtn(modalType)}
                </PrimaryButton>
                <PrimaryButton
                  variant="contained"
                  disabled={isConfirmDisabled}
                  style={{
                    ...classes.confirmBtn,
                    ...props.confirmBtnStyle,
                  }}
                  onClick={() => {
                    if (isConfirmDisabled) {
                      return;
                    }
                    onClickDelete();
                    if (closeAfterDelete) {
                      onClose();
                    }
                  }}
                >
                  {confirmButtonContent}
                </PrimaryButton>
              </>
            ) : (
              <>
                <GomakePrimaryButton
                  style={{ ...classes.confirmBtn, ...props.confirmBtnStyle }}
                  disabled={isConfirmDisabled}
                  onClick={() => {
                    if (isConfirmDisabled) {
                      return;
                    }
                    onClickDelete();
                    if (closeAfterDelete) {
                      onClose();
                    }
                  }}
                >
                  {confirmButtonContent}
                </GomakePrimaryButton>
                <GomakePrimaryButton
                  style={{ ...classes.cancelBtn, ...props.cancelBtnStyle }}
                  disabled={confirmButtonLoading}
                  onClick={handleCancelClick}
                >
                  {props?.cancelBtn || _renderCancelBtn(modalType)}
                </GomakePrimaryButton>
              </>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export { GoMakeDeleteModal };
